import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ConflictDialog } from "@/components/ConflictDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, isConflict } from "@/lib/api";
import { todayStr } from "@/lib/format";
import { Card, Fld, Sel, Txt, opts, RetPill, WarnChips } from "./RetCommon";

const blank = () => ({ return_date: todayStr(), channel: "", tracking_id: "", courier: "", qty: 1, inspection_status: "Pending" });

const Thumb = ({ f }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    api.get(`/returns/files/${f.fid}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [f.fid]);
  if (!url) return <div className="h-20 w-20 animate-pulse rounded bg-muted" />;
  return f.type.startsWith("video") ? <video src={url} controls className="h-20 w-32 rounded border" data-testid={`evidence-${f.fid}`} />
    : <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={f.name} className="h-20 w-20 rounded border object-cover" data-testid={`evidence-${f.fid}`} /></a>;
};

const Evidence = ({ r, onDone }) => {
  const [busy, setBusy] = useState(false);
  const up = async (files) => {
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/returns/${r.id}/evidence`, fd);
      }
      toast.success("Evidence uploaded"); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">{(r.evidence_files || []).filter((f) => !f.is_deleted).map((f) => <Thumb key={f.fid} f={f} />)}</div>
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium hover:bg-muted" data-testid="evidence-upload">
        <Upload className="h-3.5 w-3.5" />{busy ? "Uploading…" : "Add photos / video"}
        <input type="file" multiple accept=".jpg,.jpeg,.png,.mp4" className="hidden" disabled={busy} onChange={(e) => up([...e.target.files])} />
      </label>
    </div>
  );
};

const LiveTracking = ({ r }) => {
  const [t, setT] = useState(null);
  useEffect(() => { api.get(`/returns/${r.id}/tracking`).then((x) => setT(x.data)).catch(() => {}); }, [r.id]);
  if (!t) return null;
  return <div className="flex items-center gap-2 text-xs" data-testid="live-tracking"><Radio className="h-3.5 w-3.5 text-primary" />
    {t.available ? <span><b>Courier says:</b> {t.status}</span> : <span className="text-muted-foreground">{t.reason}</span>}</div>;
};

export const ReturnForm = ({ meta, initial, onSaved, compact }) => {
  const { can, isAdmin } = useAuth();
  const nav = useNavigate();
  const money = isAdmin || can("returns_money", "edit");
  const [f, setF] = useState(initial?.id ? initial : { ...blank(), ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  useEffect(() => { setF(initial?.id ? initial : { ...blank(), ...(initial || {}) }); }, [initial]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const o = (k) => opts(meta, k, f[k]);
  const S = (k, label, testid) => <Fld label={label}><Sel value={f[k]} onChange={set(k)} options={k === "inspection_status" ? ["Pending", "Checked"] : o(k)} testid={testid || `ret-${k}`} /></Fld>;
  const T = (k, label, type) => <Fld label={label}><Txt value={f[k]} onChange={set(k)} type={type} testid={`ret-${k}`} /></Fld>;
  const doc = f.channel === "Document";
  const save = async () => {
    if (busy) return;
    setBusy(true);
    const body = { ...f, expected_rev: f.rev || 0 };
    ["status", "next_action", "owner", "warnings", "days_open", "history", "duplicates", "shipment", "evidence_files", "closed", "id"].forEach((k) => delete body[k]);
    try {
      const { data } = f.id ? await api.put(`/returns/${f.id}`, body) : await api.post("/returns", body);
      toast.success(`${data.return_id} saved · ${data.status}`);
      onSaved ? onSaved(data) : nav(`/returns/r/${data.id}`);
    } catch (e) { if (isConflict(e)) setConflict(true); else toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reload = async () => { setConflict(false); const { data } = await api.get(`/returns/${f.id}`); setF(data); toast.success("Latest version loaded"); };
  return (
    <div className="space-y-3" data-testid="return-form">
      {f.id && <div className="flex flex-wrap items-center gap-3"><span className="font-mono text-lg font-bold">{f.return_id}</span><RetPill status={f.status} />
        {f.next_action && <span className="text-xs text-muted-foreground">Next: <b>{f.next_action}</b> ({f.owner})</span>}<WarnChips list={f.warnings} /></div>}
      {f.duplicates?.length > 0 && <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700" data-testid="dup-warning">
        Duplicate tracking ID — also on {f.duplicates.map((d) => <Link key={d.id} to={`/returns/r/${d.id}`} className="mx-1 font-mono font-bold underline">{d.return_id}</Link>)}</div>}
      <div className={compact ? "grid gap-3" : "grid gap-3 lg:grid-cols-2"}>
        <Card title="Parcel" testid="card-parcel">
          <div className="grid grid-cols-2 gap-3">{T("return_date", "Return date", "date")}{T("tracking_id", "Tracking ID")}{S("courier", "Courier")}{S("channel", "Channel")}
            {!doc && <Fld label="SKU (Product Master)"><Txt value={f.sku} onChange={set("sku")} testid="ret-sku" placeholder="optional" /></Fld>}
            {!doc && T("product_name", "Product name")}{!doc && T("qty", "Qty", "number")}{!doc && T("order_value", "Order value ₹", "number")}</div>
          {f.id && <div className="mt-3"><LiveTracking r={f} /></div>}
          {f.shipment && <Link to="/shipments" className="mt-2 block text-xs text-primary underline">Shipment {f.shipment.order_id} · {f.shipment.status}</Link>}
        </Card>
        {!doc && <Card title="Order" testid="card-order"><div className="grid grid-cols-2 gap-3">
          {f.channel !== "Website" && T("amazon_order_id", "Amazon order ID")}{f.channel !== "Amazon" && T("website_invoice_no", "Website invoice no")}</div></Card>}
        {!doc && <Card title="Reason" testid="card-reason"><div className="grid grid-cols-2 gap-3">{S("return_type", "Return type")}
          {f.return_type === "Customer Return" && S("return_reason", "Return reason")}
          <Fld label="Customer comment" className="col-span-2"><Txt value={f.customer_comment} onChange={set("customer_comment")} testid="ret-customer_comment" /></Fld></div></Card>}
        {!doc && !compact && <Card title="Inspection" testid="card-inspection"><div className="grid grid-cols-2 gap-3">{S("inspection_status", "Inspection")}
          {f.inspection_status === "Checked" && <>{S("condition", "Condition")}{S("restock_status", "Restock")}{T("inspection_date", "Inspection date", "date")}</>}
          {S("evidence", "Evidence")}</div>{f.id && <div className="mt-3"><Evidence r={f} onDone={reload} /></div>}</Card>}
        {!doc && !compact && <Card title="Refund" testid="card-refund"><div className="grid grid-cols-2 gap-3">
          <Fld label="Refund status"><Sel value={f.refund_status} onChange={set("refund_status")} options={o("refund_status")} disabled={!money} testid="ret-refund_status" /></Fld>
          {f.refund_status === "Processed" && <>{T("refund_date", "Refund date", "date")}{T("refund_amount", "Refund amount ₹", "number")}</>}</div></Card>}
        {!doc && !compact && (f.condition === "Damaged" || f.return_type === "Lost in Transit" || f.claim_raised_with) && <Card title="Claim" testid="card-claim"><div className="grid grid-cols-2 gap-3">
          <Fld label="Claim raised with"><Sel value={f.claim_raised_with} onChange={set("claim_raised_with")} options={o("claim_raised_with")} disabled={!money} testid="ret-claim_raised_with" /></Fld>
          {f.claim_raised_with && f.claim_raised_with !== "Not Required" && <>{S("claim_status", "Claim status")}{T("claim_id", "Claim ID")}{T("claim_amount", "Claim amount ₹", "number")}{T("claim_settled", "Settled ₹", "number")}</>}</div></Card>}
        <Card title="Notes" testid="card-notes"><Textarea value={f.remarks || ""} onChange={(e) => set("remarks")(e.target.value)} rows={2} data-testid="ret-remarks" />
          {f.source_ref && <div className="mt-2 text-[11px] text-muted-foreground">Imported from: {f.source_ref}</div>}</Card>
      </div>
      <div className="flex justify-end"><Button onClick={save} disabled={busy} data-testid="ret-save-btn">{busy ? "Saving…" : f.id ? "Save changes" : "Save return"}</Button></div>
      {f.history?.length > 0 && <Card title="Change history" testid="ret-history"><ul className="space-y-1 text-xs">{f.history.map((h, i) => (
        <li key={i}><span className="font-mono text-muted-foreground">{h.when.slice(0, 16).replace("T", " ")}</span> · <b>{h.user}</b> · {h.summary}</li>))}</ul></Card>}
      <ConflictDialog open={conflict} what="return" onClose={() => setConflict(false)} onReload={reload} />
    </div>
  );
};
